import { useId } from 'react'
import { useT } from '_metronic/i18n/Metronici18n'
import { MetricaIndicador } from '../../core/_models'
import { formatarContagem, formatarDuracao } from '../../core/formatacao'
import { AjudaIndicador, montarNotasAjuda } from '../ajuda/AjudaIndicador'
import { IconPaths, KTSVG } from '_metronic/helpers'

type Props = {
  /** Nome curto do indicador (vem do pt-br.json, é chrome de tela). */
  titulo: string
  metrica: MetricaIndicador
  /** Unidade lida do payload (`unidadeMedida`), não fixada no cliente. */
  unidadeMedida: string
  /**
   * Nota metodológica renderizada **como vem da API** (`rotulos.*`). Ausente ou
   * vazia, o bloco é omitido — a tela não inventa a frase que descreve o cálculo.
   */
  notaMetodologica?: string
  /** `tempoEspera.indisponivel`: o fornecedor de vídeo não registra a conexão do profissional. */
  indisponivel?: boolean
}

/**
 * Card de indicador usado por qualquer família (operacional ou qualidade).
 * Quatro estados explícitos e distintos: indisponível, sem dados no período,
 * zero legítimo e valor com denominador. Nenhum deles depende só de cor.
 *
 * A mediana é o número em destaque (menos sensível a atendimento fora da curva
 * do que a média, que fica como informação secundária). A nota metodológica vai
 * para o `?` do cabeçalho — fora da área visível, mas no `aria-describedby`.
 */
export const IndicadorCard = ({
  titulo,
  metrica,
  unidadeMedida,
  notaMetodologica,
  indisponivel,
}: Props) => {
  const { t } = useT()
  const idBase = useId()
  const idTitulo = `${idBase}-titulo`

  const media = formatarDuracao(metrica.media, unidadeMedida)
  const mediana = formatarDuracao(metrica.mediana, unidadeMedida)
  const notas = montarNotasAjuda(idBase, [notaMetodologica])

  return (
    <div
      className='card card-flush h-100 border border-gray-300'
      role='group'
      aria-labelledby={idTitulo}
      aria-describedby={notas.length > 0 ? notas.map((nota) => nota.id).join(' ') : undefined}
    >
      <div className='card-body d-flex flex-column p-6'>
        <div className='d-flex justify-content-between align-items-start gap-2 mb-3'>
          <h3 id={idTitulo} className='fs-6 fw-semibold text-gray-700 mb-0'>
            {titulo}
          </h3>
          <AjudaIndicador rotulo={t('indicadores.ajuda.rotulo', { titulo })} notas={notas} />
        </div>

        {indisponivel ? (
          <p className='d-flex align-items-start text-gray-700 fs-7 mb-0'>
            <KTSVG path={IconPaths.CircleSlash} className='svg-icon-em me-2 fs-5' ariaHidden />
            <span>{t('indicadores.card.indisponivel')}</span>
          </p>
        ) : mediana === null ? (
          <p className='d-flex align-items-center text-gray-700 fs-6 mb-0'>
            <KTSVG path={IconPaths.CircleMinus} className='svg-icon-em me-2 fs-5' ariaHidden />
            <span>{t('indicadores.card.sem_dados')}</span>
          </p>
        ) : (
          <>
            <p className='fs-8 fw-semibold text-uppercase text-gray-700 mb-1'>
              {t('indicadores.card.mediana_rotulo')}
            </p>
            <p className='fs-2hx fw-bold text-gray-900 mb-2 text-nowrap'>{mediana}</p>
            <p className='fs-7 text-gray-700 mb-0'>
              {media !== null && (
                <>
                  <span>{t('indicadores.card.media', { valor: media })}</span>
                  <span aria-hidden='true'> · </span>
                </>
              )}
              <span>{t('indicadores.card.denominador', { n: formatarContagem(metrica.n) })}</span>
            </p>
          </>
        )}
      </div>
    </div>
  )
}
